// Knowledge-base migrator: copies the contents of ONE knowledge base from a
// SOURCE bot into a TARGET bot. Built for the "Prod -> Test" utility, so callers
// pass Production as the source and the Test bot as the target — the migration
// only ever writes into the target and never touches the source.
//
// Why this is KB-scoped rather than whole-bot:
//   * A bot's file pool also holds non-KB system files (deployments/*,
//     studio/dialog-manager, integration avatars). Those must NEVER be copied,
//     so we only ever consider files tagged source === "knowledge-base".
//   * A bot can host several knowledge bases, each identified by a `kbId` tag,
//     and a file's `key` is prefixed with its kbId (e.g. "kb-default/ds-x/y.html").
//     Custom KB ids usually differ between two bots, so copying must rewrite both
//     the kbId tag and the key prefix to the chosen target KB.
//
// Flow:
//   loadKnowledgeBases        — read both bots' KB files and summarize the KBs.
//   analyzeKnowledgeBaseSync  — diff a chosen source KB against a chosen target
//                               KB (New / Changed / Unchanged / Stale), using a
//                               SHA-256 content hash to decide Changed.
//   applyKnowledgeBaseSync    — copy New + Changed into the target KB (rewriting
//                               kbId + key prefix) and optionally prune stale.

import * as BotpressAPI from "./omniBotpressAPI.js";

// Only files a knowledge base actually owns. Everything else in the file pool
// (system/deployment/integration files) is deliberately excluded.
const isKnowledgeBaseFile = file => (file?.tags?.source || "") === "knowledge-base";

// A file key is "<kbId>/<rest>". The "<rest>" is what identifies the same
// document across two KBs, so we strip the leading kbId segment when matching.
const stripKbPrefix = key => {
  const slash = (key || "").indexOf("/");
  return slash === -1 ? key || "" : key.slice(slash + 1);
};

// SHA-256 hex digest of a Blob's bytes, used to tell whether two files with the
// same identity actually differ. Runs in the browser via the Web Crypto API.
const hashBlob = async blob => {
  const buffer = await blob.arrayBuffer();
  const digest = await crypto.subtle.digest("SHA-256", buffer);
  return Array.from(new Uint8Array(digest))
    .map(byte => byte.toString(16).padStart(2, "0"))
    .join("");
};

// Derive a human-ish label for a KB from its files, since the Files API doesn't
// expose the KB's Studio name. Prefer a crawled website host, then document
// titles, then just the id.
const deriveKbLabel = (kbId, files) => {
  const hostFile = files.find(file => file.tags?.pageUrl);
  if (hostFile) {
    try {
      return `Website: ${new URL(hostFile.tags.pageUrl).host}`;
    } catch (error) {
      // fall through to other labels
    }
  }
  const titles = [...new Set(files.map(file => file.tags?.title).filter(Boolean))];
  if (titles.length > 0) {
    return titles.slice(0, 2).join(", ") + (titles.length > 2 ? "…" : "");
  }
  return kbId;
};

/**
 * Group a bot's knowledge-base files by kbId into summaries for the picker.
 * Pure/synchronous.
 *
 * @returns {Array<{ kbId, label, fileCount }>}
 */
export const summarizeKnowledgeBases = files => {
  const byKb = new Map();
  for (const file of (files || []).filter(isKnowledgeBaseFile)) {
    const kbId = file.tags.kbId || "(unknown)";
    if (!byKb.has(kbId)) {
      byKb.set(kbId, []);
    }
    byKb.get(kbId).push(file);
  }

  return [...byKb.entries()]
    .map(([kbId, kbFiles]) => ({
      kbId,
      label: deriveKbLabel(kbId, kbFiles),
      fileCount: kbFiles.length,
    }))
    .sort((a, b) => a.kbId.localeCompare(b.kbId));
};

/**
 * Read both bots' knowledge-base files (indexed KB files only) and summarize
 * the knowledge bases available on each side. The raw file lists are returned
 * too so the analyze step can reuse them without re-fetching.
 */
export const loadKnowledgeBases = async ({ sourceBotODM, targetBotODM }) => {
  if (sourceBotODM == null || targetBotODM == null) {
    throw new Error("loadKnowledgeBases: both source and target bots are required");
  }
  if (sourceBotODM.omniData.botId === targetBotODM.omniData.botId) {
    throw new Error("loadKnowledgeBases: source and target bots must be different");
  }

  const [sourceAll, targetAll] = await Promise.all([
    BotpressAPI.listAllBotpressFiles({ botODM: sourceBotODM, indexed: true }),
    BotpressAPI.listAllBotpressFiles({ botODM: targetBotODM, indexed: true }),
  ]);

  const sourceFiles = sourceAll.filter(isKnowledgeBaseFile);
  const targetFiles = targetAll.filter(isKnowledgeBaseFile);

  return {
    sourceFiles,
    targetFiles,
    sourceKbs: summarizeKnowledgeBases(sourceFiles),
    targetKbs: summarizeKnowledgeBases(targetFiles),
  };
};

// Build the descriptor used to copy a source file into the target KB: the
// rewritten target key and tags (kbId retargeted, source preserved).
const buildPlanFile = (sourceFile, targetKbId) => {
  const rest = stripKbPrefix(sourceFile.key);
  return {
    id: sourceFile.id,
    sourceKey: sourceFile.key,
    matchKey: rest,
    targetKey: `${targetKbId}/${rest}`,
    contentType: sourceFile.contentType,
    tags: { ...(sourceFile.tags || {}), kbId: targetKbId, source: "knowledge-base" },
    metadata: sourceFile.metadata || {},
  };
};

/**
 * Split the chosen source KB's files against the chosen target KB's files by
 * document identity (the key with its kbId prefix stripped). Pure/synchronous.
 */
export const classifyKbFiles = (sourceFiles, targetFiles, sourceKbId, targetKbId) => {
  const sourceInKb = (sourceFiles || []).filter(f => (f.tags?.kbId || "") === sourceKbId);
  const targetInKb = (targetFiles || []).filter(f => (f.tags?.kbId || "") === targetKbId);

  const targetByMatch = new Map(targetInKb.map(f => [stripKbPrefix(f.key), f]));
  const sourceMatchKeys = new Set(sourceInKb.map(f => stripKbPrefix(f.key)));

  const newFiles = [];
  const candidatePairs = [];

  for (const sourceFile of sourceInKb) {
    const targetFile = targetByMatch.get(stripKbPrefix(sourceFile.key));
    if (targetFile == null) {
      newFiles.push(buildPlanFile(sourceFile, targetKbId));
    } else {
      candidatePairs.push({ source: sourceFile, target: targetFile });
    }
  }

  const staleFiles = targetInKb
    .filter(f => !sourceMatchKeys.has(stripKbPrefix(f.key)))
    .map(f => ({ id: f.id, targetKey: f.key }));

  return { newFiles, candidatePairs, staleFiles };
};

/**
 * Diff a chosen source KB against a chosen target KB.
 *
 * @param {Object}   params
 * @param {Object}   params.sourceBotODM
 * @param {Object}   params.targetBotODM
 * @param {Array}    params.sourceFiles    - KB files from loadKnowledgeBases.
 * @param {Array}    params.targetFiles    - KB files from loadKnowledgeBases.
 * @param {string}   params.sourceKbId
 * @param {string}   params.targetKbId
 * @param {Function} [params.onProgress]
 * @returns {Promise<{ sourceKbId, targetKbId, newFiles, changedFiles,
 *                     unchangedCount, staleFiles }>}
 */
export const analyzeKnowledgeBaseSync = async ({
  sourceBotODM,
  targetBotODM,
  sourceFiles,
  targetFiles,
  sourceKbId,
  targetKbId,
  onProgress = () => {},
}) => {
  if (sourceBotODM.omniData.botId === targetBotODM.omniData.botId) {
    throw new Error("analyzeKnowledgeBaseSync: source and target bots must be different");
  }
  if (!sourceKbId || !targetKbId) {
    throw new Error("analyzeKnowledgeBaseSync: a source and target KB must be selected");
  }

  const { newFiles, candidatePairs, staleFiles } = classifyKbFiles(
    sourceFiles,
    targetFiles,
    sourceKbId,
    targetKbId,
  );

  const changedFiles = [];
  let unchangedCount = 0;

  for (let index = 0; index < candidatePairs.length; index++) {
    const { source, target } = candidatePairs[index];
    onProgress({ phase: "hashing", index, total: candidatePairs.length, key: source.key });

    try {
      const [sourceBlob, targetBlob] = await Promise.all([
        BotpressAPI.downloadBotpressFileContent({ botODM: sourceBotODM, fileId: source.id }),
        BotpressAPI.downloadBotpressFileContent({ botODM: targetBotODM, fileId: target.id }),
      ]);
      const [sourceHash, targetHash] = await Promise.all([
        hashBlob(sourceBlob),
        hashBlob(targetBlob),
      ]);

      if (sourceHash === targetHash) {
        unchangedCount += 1;
      } else {
        changedFiles.push(buildPlanFile(source, targetKbId));
      }
    } catch (error) {
      // If we can't read one side to compare, treat it as changed so the file
      // still gets copied rather than being silently skipped.
      console.error(`Failed to compare knowledge-base file "${source.key}":`, error);
      changedFiles.push(buildPlanFile(source, targetKbId));
    }
  }

  onProgress({ phase: "analyzed" });

  return { sourceKbId, targetKbId, newFiles, changedFiles, unchangedCount, staleFiles };
};

/**
 * Execute a plan produced by analyzeKnowledgeBaseSync: copy every New + Changed
 * file into the target bot's chosen KB (rewriting kbId + key prefix) and, when
 * `prune` is set, delete the stale target-only files. Per-file failures are
 * collected rather than aborting the whole run.
 *
 * @returns {Promise<{ created, updated, deleted, errors }>}
 */
export const applyKnowledgeBaseSync = async ({
  plan,
  sourceBotODM,
  targetBotODM,
  prune = false,
  onProgress = () => {},
}) => {
  if (sourceBotODM.omniData.botId === targetBotODM.omniData.botId) {
    throw new Error("applyKnowledgeBaseSync: source and target bots must be different");
  }

  const newFiles = plan?.newFiles || [];
  const changedFiles = plan?.changedFiles || [];
  const staleFiles = prune ? plan?.staleFiles || [] : [];

  const toCopy = [
    ...newFiles.map(file => ({ file, isNew: true })),
    ...changedFiles.map(file => ({ file, isNew: false })),
  ];

  const total = toCopy.length + staleFiles.length;
  const errors = [];
  let created = 0;
  let updated = 0;
  let deleted = 0;
  let index = 0;

  for (const { file, isNew } of toCopy) {
    onProgress({ phase: "copying", index, total, key: file.targetKey });
    index += 1;

    try {
      const blob = await BotpressAPI.downloadBotpressFileContent({
        botODM: sourceBotODM,
        fileId: file.id,
      });

      const result = await BotpressAPI.upsertBotpressFile({
        botODM: targetBotODM,
        key: file.targetKey,
        blob,
        contentType: file.contentType,
        index: true,
        tags: file.tags,
        metadata: file.metadata,
      });

      if (result && Object.hasOwn(result, "code")) {
        errors.push({
          key: file.targetKey,
          message: result.message || `Error code ${result.code}`,
        });
      } else if (isNew) {
        created += 1;
      } else {
        updated += 1;
      }
    } catch (error) {
      console.error(`Failed to copy knowledge-base file "${file.targetKey}":`, error);
      errors.push({ key: file.targetKey, message: error.message || "Copy failed" });
    }
  }

  for (const file of staleFiles) {
    onProgress({ phase: "deleting", index, total, key: file.targetKey });
    index += 1;

    try {
      await BotpressAPI.deleteBotpressFile({ botODM: targetBotODM, fileId: file.id });
      deleted += 1;
    } catch (error) {
      console.error(`Failed to delete stale knowledge-base file "${file.targetKey}":`, error);
      errors.push({ key: file.targetKey, message: error.message || "Delete failed" });
    }
  }

  onProgress({ phase: "done", total });

  return { created, updated, deleted, errors };
};
